import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchDistrictActive,
  fetchDistrictHistory,
  disableDistrict,
  updateDistrict,
  revokeDistrict,
} from "../api/accessControl.api";
import {
  mapFlagToRestriction,
  mapFlagsToAuditHistory,
} from "../utils/accessControl.utils";
import { QUERY_KEYS } from "../constants";

export function useDistrictAccess() {
  const queryClient = useQueryClient();

  const activeQuery = useQuery({
    queryKey: QUERY_KEYS.districtActive,
    queryFn: fetchDistrictActive,
    select: (flag) => {
      if (!flag) return null;
      const restriction = mapFlagToRestriction(flag);
      const hasStarted =
        !restriction.start || new Date(restriction.start) <= new Date();
      return hasStarted ? restriction : null;
    },
  });

  const historyQuery = useQuery({
    queryKey: QUERY_KEYS.districtHistory,
    queryFn: fetchDistrictHistory,
    select: mapFlagsToAuditHistory,
  });

  const createMutation = useMutation({
    mutationFn: (dto) => disableDistrict(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.districtActive });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.districtHistory });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (dto) => updateDistrict(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.districtActive });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.districtHistory });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (dto) => revokeDistrict(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.districtActive });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.districtHistory });
    },
  });

  const extractError = (err, fallback) => {
    if (!err) return null;
    const data = err?.response?.data;
    if (data?.code === "ACTIVE_DISABLE_EXISTS") {
      return "A district pause is already active. End it before creating a new one.";
    }
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(", ");
    if (typeof msg === "string") return msg;
    return fallback;
  };

  return {
    activeRestriction: activeQuery.data ?? null,
    auditHistory: historyQuery.data ?? [],
    isLoading: activeQuery.isLoading || historyQuery.isLoading,
    isError: activeQuery.isError || historyQuery.isError,

    create: createMutation.mutate,
    update: updateMutation.mutate,
    revoke: revokeMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRevoking: revokeMutation.isPending,

    createError: extractError(
      createMutation.error,
      "Failed to schedule district pause. Please try again.",
    ),
    updateError: extractError(
      updateMutation.error,
      "Failed to update district pause. Please try again.",
    ),
    revokeError: extractError(
      revokeMutation.error,
      "Failed to end district pause. Please try again.",
    ),
  };
}
